import { Dialog, DialogContent, DialogTitle, Divider } from '@mui/material';
import {
  createUsersBusinessesPortfolio,
  postCollectionManagement,
} from 'lib/api';
import {
  getCollectionManagementOptionsByStateId,
  getCollectionManagementStates,
} from 'lib/api/collectionManagementStates';
import { getExecutivesForDocumentCollection } from 'lib/api/collections';
import { getDocumentReport } from 'lib/api/document';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import TwoColumnDocumentResume from 'lib/designSystem/TwoColumnDocumentResume';
import { loggerDev, showAlert } from 'lib/functions/utils';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Actions } from './components/Actions';
import { CollectionState, OptionsGrid } from './components/OptionsGrid';
interface propsType {
  openModal: boolean;
  setOpenModal: any;
  completed?: boolean;
  setUp: any;
  documentId: string;
  triggerSetUpWhenClose?: boolean;
}

const NewManagement: React.FC<propsType> = (props: propsType) => {
  const { completed, documentId, setOpenModal, setUp, triggerSetUpWhenClose } =
    props;
  const [option, setOption] = useState<any>(null);
  const [description, setDescription] = useState('');
  const [checkFields, setCheckField] = useState(false);
  const [options, setOptions] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [stateId, setStateId] = useState<number>(0);
  const [stateName, setStateName] = useState('');
  const [stateOptions, setStateOptions] = useState<CollectionState[]>([]);
  const [completedCheckbox, setCompletedCheckbox] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contactMethod, setContactMethod] = useState('');
  const today = moment()
    .utcOffset(new Date().getTimezoneOffset() * -1)
    .format('YYYY-MM-DD');
  const [dueDate, setDueDate] = useState(today);
  const [completedDate, setCompletedDate] = useState(today);
  const [documentHasExecutives, setDocumentHasExecutives] = useState(false);
  const [executivesOptions, setExecutivesOptions] = useState([]);
  const [executiveId, setExecutiveId] = useState(0);
  const [isManager, setIsManager] = useState(false);
  const [byStakeholder, setByStakeholder] = useState(false);
  const [portfolioExecutiveId, setPortfolioExecutiveId] = useState(0);
  const [possiblePaymentDate, setPossiblePaymentDate] = useState('none');
  const dispatch = useDispatch();

  const { data: document } = getDocumentReport(dispatch, documentId);

  const checkInputFields = () => {
    if (
      !description.length ||
      option === '' ||
      stateId === 0 ||
      contactMethod === '' ||
      dueDate === ''
    ) {
      return true;
    }
    return false;
  };

  const handleSend = async () => {
    setCheckField(checkInputFields());
    if (checkInputFields()) return;
    setLoading(true);
    try {
      await postCollectionManagement(dispatch, {
        description: description,
        type: contactMethod,
        optionId: option,
        documentId: documentId,
        state: stateName,
        dueDate: new Date(dueDate).toISOString(),
        completedAt: completedCheckbox
          ? new Date(completedDate).toISOString()
          : null,
        executiveId: executiveId,
        possiblePaymentDate: possiblePaymentDate,
      });
      setOpenModal(false);
      showAlert(dispatch, 'success', 'Tu gestión de cobranza ha sido creada');
      await setUp();
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Hubo un error al crear la gestión de cobranza',
      );
      loggerDev(error);
    }
    setLoading(false);
  };

  const handleClose = () => {
    if (triggerSetUpWhenClose) {
      void setUp();
    }
    setOpenModal(false);
  };

  const handleSelectState = async (value: number) => {
    setDisabled(true);
    setStateId(value);
    const selectedState = stateOptions.find((element) => element.id === value);
    setStateName(selectedState ? selectedState.name : '');
    await getOptions(value);
    setDisabled(false);
  };

  const getOptions = async (selectedStateId: number) => {
    setLoading(true);
    try {
      const result = await getCollectionManagementOptionsByStateId(
        dispatch,
        selectedStateId,
      );
      setOptions(result);
    } catch (error) {
      loggerDev(error);
    }
    setLoading(false);
  };

  const handleAssignExecutive = async () => {
    setLoading(true);
    try {
      await createUsersBusinessesPortfolio(dispatch, portfolioExecutiveId, {
        businessIdentifiers: byStakeholder
          ? [document?.stakeholderIdentifier]
          : [document?.businessIdentifier],
      });
      showAlert(dispatch, 'success', 'Ejecutivo asignado correctamente');
      void setUpOptions(document);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al asignar ejecutivo');
      loggerDev(error);
    }
    setLoading(false);
  };

  const setUpOptions = async (document: any) => {
    try {
      const result = await getCollectionManagementStates(dispatch);
      setStateOptions(result);
      const query = `businessIdentifier=${document.businessIdentifier}&stakeholderIdentifier=${document.stakeholderIdentifier}`;
      const resultExecutives = await getExecutivesForDocumentCollection(
        dispatch,
        query,
      );
      setDocumentHasExecutives(resultExecutives.documentHasExecutives);
      setExecutivesOptions(resultExecutives.executives);
      setIsManager(resultExecutives.isManager);
      setByStakeholder(resultExecutives.byStakeholder);
      setPortfolioExecutiveId(0);
      setExecutiveId(0);
    } catch (error) {
      console.error(error);
      loggerDev(error);
    }
  };

  const setUpDialog = async () => {
    setLoading(true);
    await setUpOptions(document);
    setLoading(false);
  };

  useEffect(() => {
    if (document) {
      void setUpDialog();
    }
  }, [document]);

  if (loading || !document) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog open={props.openModal} maxWidth="md">
      <DialogTitle> Crear nueva gestión de cobranza</DialogTitle>
      <DialogContent style={{ minWidth: '500px' }}>
        <TwoColumnDocumentResume document={document} maxBusinessLength={40} />
        <Divider style={{ margin: '12px 0px' }} />
        <OptionsGrid
          executiveId={executiveId}
          documentHasExecutives={documentHasExecutives}
          byStakeholder={byStakeholder}
          document={document}
          checkFields={checkFields}
          option={option}
          setOption={setOption}
          setExecutiveId={setExecutiveId}
          loading={loading}
          executivesOptions={executivesOptions}
          portfolioExecutiveId={portfolioExecutiveId}
          setPortfolioExecutiveId={setPortfolioExecutiveId}
          stateId={stateId}
          handleSelectState={handleSelectState}
          stateOptions={stateOptions}
          isManager={isManager}
          completedCheckbox={completedCheckbox}
          setCompletedCheckbox={setCompletedCheckbox}
          completedDate={completedDate}
          dueDate={dueDate}
          description={description}
          setDescription={setDescription}
          contactMethod={contactMethod}
          disabled={disabled}
          completed={completed}
          setDueDate={setDueDate}
          setCompletedDate={setCompletedDate}
          setContactMethod={setContactMethod}
          options={options}
          possiblePaymentDate={possiblePaymentDate}
          setPossiblePaymentDate={setPossiblePaymentDate}
        />
      </DialogContent>
      <Actions
        handleClose={handleClose}
        documentHasExecutives={documentHasExecutives}
        handleSend={handleSend}
        loading={loading}
        stateId={stateId}
        option={option}
        contactMethod={contactMethod}
        description={description}
        dueDate={dueDate}
        handleAssignExecutive={handleAssignExecutive}
        portfolioExecutiveId={portfolioExecutiveId}
      />
    </Dialog>
  );
};

export default NewManagement;
